import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../API/api';

// Async thunk to create a new referral
export const createReferral = createAsyncThunk(
  'referral/createReferral',
  async ({ user_id, referral_code }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/referrals', { user_id, referral_code });
      // Update user state in Redux
      dispatch({
        type: 'user/updateUser',
        payload: response.data.user,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create referral');
    }
  }
);

// Async thunk to fetch referral status
export const fetchReferralStatus = createAsyncThunk(
  'referral/fetchReferralStatus',
  async (referrer_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/referrals/${referrer_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch referral status');
    }
  }
);

// Async thunk to collect referral prize
export const collectReferralPrize = createAsyncThunk(
  'referral/collectReferralPrize',
  async (referrer_id, { rejectWithValue }) => {
    try {
      const response = await api.post('/referrals/collect-prize', { referrer_id });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to collect referral prize');
    }
  }
);

// Async thunk to fetch referral stats
export const fetchReferralStats = createAsyncThunk(
  'referral/fetchReferralStats',
  async (referrer_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/referrals/${referrer_id}/stats`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch referral stats');
    }
  }
);

// Async thunk to fetch reward history
export const fetchRewardHistory = createAsyncThunk(
  'referral/fetchRewardHistory',
  async (referrer_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/referrals/${referrer_id}/reward-history`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch reward history');
    }
  }
);

const referralSlice = createSlice({
  name: 'referral',
  initialState: {
    referralStatus: null,
    referralStats: null,
    rewardHistory: [],
    notifications: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearNotification: (state, action) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Referral
      .addCase(createReferral.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReferral.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications.push({
          id: Date.now(),
          message: action.payload.message,
          type: 'success',
        });
        if (state.notifications.length > 5) {
          state.notifications.shift();
        }
      })
      .addCase(createReferral.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.notifications.push({
          id: Date.now(),
          message: action.payload,
          type: 'error',
        });
        if (state.notifications.length > 5) {
          state.notifications.shift();
        }
      })
      // Fetch Referral Status
      .addCase(fetchReferralStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReferralStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.referralStatus = action.payload;
      })
      .addCase(fetchReferralStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Collect Referral Prize
      .addCase(collectReferralPrize.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(collectReferralPrize.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications.push({
          id: Date.now(),
          message: action.payload.message,
          type: 'success',
        });
        if (state.notifications.length > 5) {
          state.notifications.shift();
        }
      })
      .addCase(collectReferralPrize.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.notifications.push({
          id: Date.now(),
          message: action.payload,
          type: 'error',
        });
        if (state.notifications.length > 5) {
          state.notifications.shift();
        }
      })
      // Fetch Referral Stats
      .addCase(fetchReferralStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReferralStats.fulfilled, (state, action) => {
        state.loading = false;
        state.referralStats = action.payload;
      })
      .addCase(fetchReferralStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Reward History
      .addCase(fetchRewardHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRewardHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.rewardHistory = action.payload;
      })
      .addCase(fetchRewardHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearNotification } = referralSlice.actions;
export default referralSlice.reducer;